import { Box } from "@mui/system"
import avatarPixar from '/avatar_pixar_female.webp'
import useStyles from "./styles.ts"
import { Avatar, useTheme } from "@mui/material"
import { useSelector } from "react-redux"
import * as ScenarioSelectors from "app/selectors/scenarioSelectors.ts";
import * as UserSelectors from "app/selectors/userSelectors.ts";
import { AudioChatSelector } from "app/store/AudioChat/index.ts"
import LanguageSwitcher from "./LanguageSwitcher.tsx"
import {
  selectedSourceLanguageCode,
  selectedTargetLanguageCode,
  selectedSourceTwoLetterCode,
  selectedTargetTwoLetterCode
} from '@/features/translator/signals.ts'
import { type ScenarioDto } from '@/api-gen/api.schemas'
import { useAvatarLogic } from 'app/hooks/useAvatarLogic.ts'
import { useMemo } from 'react'

type TalkingAIProps = {
    isListening?: boolean;
    isSpeaking?: boolean;
    height?: number;
    width?: number;
    portraitSrc?: string;
    isLoading?: boolean;
    isTranslator?: boolean;
    onLanguageSwitch?: (newSourceLanguageCode: string) => void;
    /** Optional scenario override. If provided, avatar logic uses this scenario instead of store-selected. */
    scenario?: ScenarioDto;
}

const TalkingAI = ({ isListening, isSpeaking, width = 396, height = 282, portraitSrc, isLoading, isTranslator = false, onLanguageSwitch, scenario: scenarioProp }: TalkingAIProps) => {
    const classes = useStyles();
    const theme = useTheme();
    const user = useSelector(UserSelectors.CurrentUserSelector);
    const currentScenarioLink = useSelector(ScenarioSelectors.currentScenario);

    const scenarioFromStore: ScenarioDto | undefined = useSelector(
        useMemo(
            () => (state) => currentScenarioLink?.sceneId
                ? ScenarioSelectors.getScenarioById(currentScenarioLink.sceneId)(state)
                : undefined,
            [currentScenarioLink?.sceneId]
        )
    );
    // Prefer explicit prop if provided (e.g., Scenario Player), fallback to store-selected scenario (Editor)
    const scenario: ScenarioDto | undefined = scenarioProp ?? scenarioFromStore;

    const transcript = useSelector(AudioChatSelector.transcriptSelector);

    const userInitials = user.name
        .split(' ')
        .map(part => part[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);

    const { effectivePortraitSrc } = useAvatarLogic({ scenario, transcript, portraitSrc });

    const avatar = (
        <Avatar className={`${classes.portraitSvg} ${isSpeaking ? 'animated' : ''}`} sx={{ width: width * 0.485, height: height * 0.68, backgroundColor: theme.palette.primary[400] }}>
            {userInitials}
        </Avatar>)

    return (
        <Box sx={{ position: 'relative', width: width, height: height }}>
            <svg className={classes.backgroundSvg} width={width} height={height} viewBox={`0 0 396 282`} fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M346.706 59.957C340.449 59.957 335.359 65.1057 335.359 71.4348V212.324C335.932 227.555 357.526 227.501 358.095 212.324V71.4348C358.095 65.1062 352.986 59.957 346.706 59.957Z" fill="#FF9900" fillOpacity="0.2" />
                <path d="M272.46 59.957C266.179 59.957 261.07 65.1057 261.07 71.4348V212.324C261.644 227.534 283.281 227.522 283.85 212.324V71.4348C283.85 65.1062 278.74 59.957 272.46 59.957Z" fill="#FF9900" fillOpacity="0.2" />
                <path d="M309.582 101.926C303.326 101.926 298.236 107.075 298.236 113.404V170.358C298.809 185.59 320.403 185.534 320.971 170.358V113.404C320.971 107.075 315.862 101.926 309.582 101.926Z" fill="#FF9900" fillOpacity="0.2" />
                <path d="M383.826 122.91C377.57 122.91 372.48 128.058 372.48 134.387V149.373C373.053 164.605 394.647 164.549 395.216 149.373V134.387C395.216 128.058 390.107 122.91 383.826 122.91Z" fill="#FF9900" fillOpacity="0.2" />
                <path d="M235.343 17.9897C229.062 17.9897 223.953 23.1385 223.953 29.4675V254.293C224.527 269.48 246.121 269.513 246.688 254.293V29.4675C246.688 23.1385 241.598 17.9897 235.343 17.9897Z" fill="#FF9900" fillOpacity="0.2" />
                <path d="M49.2905 59.9575C55.5469 59.9575 60.6367 65.1062 60.6367 71.4353V212.325C60.0642 227.556 38.4698 227.501 37.9011 212.325V71.4353C37.9011 65.1067 43.0101 59.9575 49.2905 59.9575Z" fill="#FF9900" fillOpacity="0.2" />
                <path d="M123.538 59.9575C129.819 59.9575 134.928 65.1062 134.928 71.4353V212.325C134.354 227.535 112.717 227.523 112.148 212.325V71.4353C112.148 65.1067 117.258 59.9575 123.538 59.9575Z" fill="#FF9900" fillOpacity="0.2" />
                <path d="M86.4164 101.926C92.6724 101.926 97.7617 107.075 97.7617 113.404V170.358C97.1892 185.59 75.5952 185.534 75.0266 170.358V113.404C75.0266 107.075 80.136 101.926 86.4164 101.926Z" fill="#FF9900" fillOpacity="0.2" />
                <path d="M12.1718 123.792C18.4278 123.792 23.5176 128.941 23.5176 135.27V150.256C22.9451 165.488 1.35109 165.432 0.782415 150.256V135.27C0.782415 128.941 5.89141 123.792 12.1718 123.792Z" fill="#FF9900" fillOpacity="0.2" />
                <path d="M160.656 17.9897C166.936 17.9897 172.045 23.1385 172.045 29.4675V254.293C171.471 269.48 149.877 269.513 149.31 254.293V29.4675C149.31 23.1385 154.4 17.9897 160.656 17.9897Z" fill="#FF9900" fillOpacity="0.2" />
                <path d="M196.818 0.92334C203.098 0.92334 208.207 6.74528 208.207 13.9019V268.125C207.633 285.298 186.039 285.335 185.472 268.125V13.9019C185.472 6.74528 190.562 0.92334 196.818 0.92334Z" fill="#FF9900" fillOpacity="0.2" />
            </svg>
            <svg className={`${classes.backgroundSvg} ${isListening ? 'animated' : ''} ${isLoading ? 'loading' : ''}`} width={width * 0.555} height={height * 0.78} viewBox="0 0 220 220">
                <circle cx="110" cy="110" r="110" fill={"#FF9900"} />
            </svg>

            {isSpeaking && (
                <svg className={`${classes.bubbleSvg} ${isSpeaking ? 'animated' : ''}`} width={width * 0.57} height={height * 0.808} viewBox="0 0 226 228" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M49.0422 208.878C58.7636 218.058 69.5118 220.848 83.9172 221.705C96.083 222.428 121.719 231.829 144.669 225.079C167.62 218.329 163.795 209.329 186.296 195.828C208.796 182.328 219.372 161.177 220.497 148.577C221.622 135.976 224.547 109.651 225.222 88.2751C225.897 66.8994 202.946 53.624 189.671 31.7984C176.395 9.97275 152.77 5.0226 146.469 5.24761C140.169 5.47261 129.369 5.02247 107.768 1.42236C86.1677 -2.17775 69.7422 4.79709 63.667 10.8723C57.5918 16.9475 47.9166 25.0481 32.1661 36.9735C16.4156 48.8988 11.6905 56.774 7.86537 64.8743C4.04025 72.9745 3.59157 93.9 3.14156 102C2.69155 110.1 -0.235427 127.426 1.11461 141.602C2.46465 155.777 20.24 174.228 23.3901 177.603C26.5402 180.978 36.8905 197.403 49.0422 208.878Z" fill="#FF9900" />
                </svg>
            )}
            {!isLoading && !isTranslator && (
                <img className={`${classes.portraitSvg} ${isSpeaking ? 'animated' : ''}`} width={width * 0.485} height={height * 0.68} src={effectivePortraitSrc}></img>
            )}
            {!isLoading && isTranslator && (
                <Box className={classes.portraitSvg} width={width * 0.485} height={height * 0.68}>
                    <LanguageSwitcher 
                        width={width * 0.485} 
                        height={height * 0.68}
                        sourceLanguage={selectedSourceLanguageCode.value} 
                        targetLanguage={selectedTargetLanguageCode.value}
                        sourceTwoLetterCode={selectedSourceTwoLetterCode.value}
                        targetTwoLetterCode={selectedTargetTwoLetterCode.value}
                        onClick={() => {
                            if (onLanguageSwitch && selectedTargetTwoLetterCode.value) {
                                onLanguageSwitch(selectedTargetTwoLetterCode.value);
                            }
                        }}
                    />
                </Box>
            )}
        </Box>);
}

export default TalkingAI
